import React, { ReactNode, useMemo } from 'react';
import { Combobox } from '@mantine/core';
import { MdCheck } from 'react-icons/md';
import InfiniteScroll, { Props as IInfiniteScrollProps } from 'react-infinite-scroll-component';
import type { IOption } from '../../types/IOption';
import Checkbox from '../Checkbox/Checkbox';
import clsx from 'clsx';
import Skeleton from '../Skeleton';
import { MenuVariantSize } from './Select.types';
// Список отдельно от поля (выпадающие списки пикеров) стилизуется теми же правилами.
import './Select.scss';

// Ширины скелетона текста — детерминированная вариация «строк разной длины».
const SKELETON_WIDTHS = ['56%', '70%', '48%'];

export interface ISelectMenuProps {
    /**
     * Ссылка на элемент
     */
    ref?: React.Ref<HTMLUListElement>;
    options: IOption[];
    selected: IOption[];
    multiselect: boolean;
    selectId: number;
    menuVariantSize: MenuVariantSize;
    isAsync?: boolean;
    infinityScrollProps?: Omit<IInfiniteScrollProps, 'children' | 'next' | 'scrollableTarget' | 'loader'>;
    /** Набранный поиск — совпадение подсвечивается в подписях пунктов */
    query?: string;
    loading: boolean;
    onSearch?: (query: string, isPagination?: boolean) => void;
    onScroll?: (e: React.UIEvent) => void;
    /**
     * Standalone-режим (вне Combobox-контекста Select): клики по опциям обрабатываются
     * этим колбэком, элементы рендерятся легаси-разметкой li+button (DictpickerSelect
     * встраивает меню в собственный floating-дропдаун).
     */
    onOptionClick?: (option: IOption) => Promise<void> | void;
    /** Закрытие в standalone-режиме (Escape). */
    onClose?: () => void;
    /** Выравнивание пунктов: `start` — по левому краю. @default 'center' */
    listItemAlign?: 'center' | 'start';
}

// Наполнение дропдауна Select: опции — Combobox.Option (value = индекс в
// options; клавиатура/highlight/сабмит — стор Combobox, самодельный moveFocus по li
// удалён). Чекбокс в multiselect — чисто визуальный (controlled без onChange): клик
// по нему всплывает до Combobox.Option и уходит в onOptionSubmit фасада.
const SelectMenu: React.FC<ISelectMenuProps> = ({
    ref,
    options,
    selected,
    multiselect,
    selectId,
    menuVariantSize,
    isAsync,
    infinityScrollProps,
    query = '',
    loading,
    onSearch,
    onScroll,
    onOptionClick,
    onClose,
    listItemAlign = 'center',
}) => {
    const hasInfinityScroll = typeof onSearch === 'function';
    const standalone = typeof onOptionClick === 'function';

    // Standalone-ветка живёт вне Combobox-стора, клавиатуру которого получает обычный Select.
    // Восстанавливаем легаси-поведение (FloatingFocusManager + handleKeyDown до Волны 9):
    // фокус при открытии уходит в список, стрелки ходят по опциям, Enter/Space выбирают.
    const listRef = React.useRef<HTMLUListElement | null>(null);
    const setListRef = React.useCallback(
        (el: HTMLUListElement | null) => {
            listRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) (ref as React.RefObject<HTMLUListElement | null>).current = el;
        },
        [ref],
    );

    React.useEffect(() => {
        if (!standalone || loading) return;
        const list = listRef.current;
        if (!list || list.contains(list.ownerDocument.activeElement)) return;
        const first = list.querySelector<HTMLElement>('li[role="option"]');
        (first ?? list).focus({ preventScroll: true });
    }, [standalone, loading, options.length]);

    const moveOptionFocus = (list: HTMLUListElement, delta: 1 | -1) => {
        const items = Array.from(list.querySelectorAll<HTMLElement>('li[role="option"]'));
        if (!items.length) return;
        const active = list.ownerDocument.activeElement as HTMLElement | null;
        const idx = items.findIndex((el) => el === active);
        const next =
            idx === -1 ? (delta === 1 ? 0 : items.length - 1) : Math.min(items.length - 1, Math.max(0, idx + delta));
        items[next]?.focus();
    };

    const makeLazyFetch = () => {
        if (onSearch && isAsync) {
            return () => onSearch(query, isAsync);
        }

        return () => undefined;
    };

    // Первичная загрузка опций — скелетон строк (форма списка известна); догрузка
    // по infinite-scroll — пара строк снизу. Класс rf-select__preloader сохранён
    // (на него завязаны стиль и тест). Каждая строка = блок в размер реального пункта
    // (фон выделенной опции) со скелетоном текста внутри.
    const skeletonRows = (count: number) =>
        Array.from({ length: count }).map((_, i) => (
            <div className="rf-select__skeleton-item" key={i}>
                <div className="rf-select__skeleton-block">
                    <Skeleton height={12} width={SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]} radius="sm" />
                </div>
            </div>
        ));

    const optionsSkeleton = (
        <div className="rf-select__preloader rf-select__preloader--skeleton" aria-hidden="true">
            {skeletonRows(3)}
        </div>
    );

    const moreSkeleton = (
        <div className="rf-select__preloader rf-select__preloader--skeleton" aria-hidden="true">
            {skeletonRows(2)}
        </div>
    );

    const selectedMap = useMemo((): Record<string, boolean> => {
        return selected.reduce((acc: Record<string, boolean>, o: IOption) => {
            acc[o.value] = true;
            return acc;
        }, {});
    }, [selected]);

    const listJSX = options.map((option, index) => {
        const active = selectedMap[option.value] || false;

        let label: ReactNode = option.label;

        // TODO: думаю это можно вынести в отдельный компонент (Highlighter)
        if (query) {
            const indexStart = option.label.toLowerCase().indexOf(query.toLowerCase());

            if (indexStart > -1) {
                const indexEnd = indexStart + query.length - 1;
                let left = '';
                let highlighted = '';
                let right = '';

                for (let i = 0; i < option.label.length; i++) {
                    if (i < indexStart) {
                        left += option.label[i];
                        continue;
                    }

                    if (i >= indexStart && i <= indexEnd) {
                        highlighted += option.label[i];
                        continue;
                    }

                    right += option.label[i];
                }

                label = (
                    <>
                        {left}
                        <span title={highlighted} className="rf-select__list-element--query">
                            {highlighted}
                        </span>
                        {right}
                    </>
                );
            }
        }

        // Выбранный пункт одиночного выбора отмечен галочкой (и цветом в стилях): подсветка подстроки —
        // только для набранного поиска, иначе выбранное «допущен» подсвечивалось бы и в «Не допущен».
        const content = (
            <>
                {label}
                {active && <MdCheck className="rf-select__check" aria-hidden="true" />}
            </>
        );

        const itemClassName = clsx(
            {
                'rf-select__list-element': true,
                'rf-select__list-element--disabled': option.disabled,
                'rf-select__list-element--active': active,
            },
            option.cssClass,
        );

        if (standalone) {
            return (
                <li
                    key={index}
                    role="option"
                    tabIndex={0}
                    aria-selected={active}
                    aria-disabled={option.disabled ? 'true' : undefined}
                    data-testid={`rf-select__list-element-${option.value}`}
                    className={itemClassName}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            if (!option.disabled) void onOptionClick?.(option);
                        }
                    }}
                >
                    <button
                        tabIndex={-1}
                        type="button"
                        title={option.label}
                        className={
                            option.disabled || listItemAlign === 'start'
                                ? 'rf-select__list-element-single-left'
                                : 'rf-select__list-element-single'
                        }
                        onClick={(event) => {
                            event.stopPropagation();
                            void onOptionClick?.(option);
                        }}
                    >
                        {content}
                    </button>
                </li>
            );
        }

        return (
            <Combobox.Option
                key={index}
                value={String(index)}
                active={active}
                disabled={option.disabled}
                title={option.label}
                data-testid={`rf-select__list-element-${option.value}`}
                className={itemClassName}
            >
                {multiselect && !option.disabled ? (
                    <Checkbox title={option.label} label={label} checked={active} readOnly fullWidth />
                ) : (
                    <span
                        title={option.label}
                        className={
                            option.disabled || listItemAlign === 'start'
                                ? 'rf-select__list-element-single-left'
                                : 'rf-select__list-element-single'
                        }
                    >
                        {content}
                    </span>
                )}
            </Combobox.Option>
        );
    });

    const content = hasInfinityScroll ? (
        <InfiniteScroll
            dataLength={0}
            hasMore={false}
            {...infinityScrollProps}
            next={makeLazyFetch()}
            loader={moreSkeleton}
            scrollableTarget={`Select-${selectId}-list-scroll`}
            className="rf-select__infinity-list"
        >
            {listJSX}
        </InfiniteScroll>
    ) : (
        <>{loading ? optionsSkeleton : listJSX}</>
    );

    if (standalone) {
        return (
            <ul
                ref={setListRef}
                data-testid="rf-select-list-scroll"
                className={clsx('rf-select__list', `rf-select__list--${menuVariantSize}`)}
                id={`Select-${selectId}-list-scroll`}
                onScroll={onScroll}
                onKeyDown={(event) => {
                    if (event.key === 'Escape' || event.code === 'Tab') {
                        event.preventDefault();
                        onClose?.();
                        return;
                    }
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        moveOptionFocus(event.currentTarget, event.key === 'ArrowDown' ? 1 : -1);
                    }
                }}
                role="listbox"
                tabIndex={0}
            >
                {content}
            </ul>
        );
    }

    // Combobox.Options регистрирует listId в сторе — без него клавиатурная навигация
    // (selectNextOption и т.п.) не находит опции.
    return (
        <Combobox.Options
            ref={ref as React.Ref<HTMLDivElement>}
            data-testid="rf-select-list-scroll"
            className={clsx('rf-select__list', `rf-select__list--${menuVariantSize}`)}
            id={`Select-${selectId}-list-scroll`}
            onScroll={onScroll}
        >
            {content}
        </Combobox.Options>
    );
};

export default SelectMenu;
